import React from "react";
import { usePhysicsStore } from "../stores/physicsStore";
import { hasPhysicsWorker } from "../physics/workerHandle";
import { logger } from "./logger";

const SCENE_AUTHORING_BLOCKED_TOAST_MS = 1800;
const READ_ONLY_PROJECT_LOCK_MESSAGE = "Read-only project. Edit controls are disabled.";
let lastSceneAuthoringBlockedToastAt = 0;
let readOnlyProjectMode = false;
const readOnlyProjectModeListeners = new Set<() => void>();

const isPhysicsRuntimeAuthoringLockedState = (state: ReturnType<typeof usePhysicsStore.getState>) =>
  state.status === "starting" ||
  state.status === "running" ||
  state.status === "paused" ||
  state.isRunning === true ||
  state.isPaused === true ||
  hasPhysicsWorker();

const subscribeReadOnlyProjectMode = (listener: () => void) => {
  readOnlyProjectModeListeners.add(listener);
  return () => readOnlyProjectModeListeners.delete(listener);
};

const getReadOnlyProjectModeSnapshot = () => readOnlyProjectMode;

const showSceneAuthoringBlockedToast = () => {
  if (typeof window === "undefined") return;
  const now = Date.now();
  if (now - lastSceneAuthoringBlockedToastAt < SCENE_AUTHORING_BLOCKED_TOAST_MS) return;
  lastSceneAuthoringBlockedToastAt = now;
  try {
    window.dispatchEvent(
      new CustomEvent("critter-toast", {
        detail: { message: "Pause physics to edit the scene" },
      })
    );
  } catch {
    // Toasts are best-effort UI feedback; the guard still blocks the mutation.
  }
};

const showReadOnlyProjectBlockedToast = () => {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent("critter-toast", {
        detail: { message: READ_ONLY_PROJECT_LOCK_MESSAGE },
      })
    );
  } catch {
    // Toasts are best-effort UI feedback; the guard still blocks the mutation.
  }
};

export const isPhysicsRunning = (): boolean => usePhysicsStore.getState().isRunning === true;

export const isReadOnlyProjectMode = (): boolean => readOnlyProjectMode;

export const setReadOnlyProjectMode = (locked: boolean): void => {
  if (readOnlyProjectMode === locked) return;
  readOnlyProjectMode = locked;
  readOnlyProjectModeListeners.forEach((listener) => listener());
};

export const useReadOnlyProjectMode = (): boolean =>
  React.useSyncExternalStore(
    subscribeReadOnlyProjectMode,
    getReadOnlyProjectModeSnapshot,
    getReadOnlyProjectModeSnapshot
  );

export const isPhysicsAuthoringLocked = (): boolean => {
  return readOnlyProjectMode || isPhysicsRuntimeAuthoringLockedState(usePhysicsStore.getState());
};

export const isPhysicsRuntimeAuthoringLocked = (): boolean =>
  isPhysicsRuntimeAuthoringLockedState(usePhysicsStore.getState());

export const usePhysicsAuthoringLock = (): boolean => {
  const runtimeLocked = usePhysicsStore(isPhysicsRuntimeAuthoringLockedState);
  const readOnlyLocked = useReadOnlyProjectMode();
  return runtimeLocked || readOnlyLocked;
};

export const usePhysicsRuntimeAuthoringLock = (): boolean =>
  usePhysicsStore(isPhysicsRuntimeAuthoringLockedState);

export const getPhysicsBlockedActionTitle = (
  isBlocked: boolean,
  actionPhrase: string
): string | undefined => (isBlocked ? `Stop physics to ${actionPhrase}` : undefined);

export const getTransformEditorBlockProps = (isBlocked: boolean) => ({
  disabled: isBlocked,
  title: getPhysicsBlockedActionTitle(isBlocked, "edit transforms"),
});

export const getPhysicsAuthoringLockMessage = (actionPhrase = "edit the scene"): string =>
  readOnlyProjectMode ? READ_ONLY_PROJECT_LOCK_MESSAGE : `Stop physics to ${actionPhrase}.`;

export const getPhysicsAuthoringLockProps = (
  isBlocked: boolean,
  actionPhrase = "edit the scene"
) => ({
  disabled: isBlocked,
  title: isBlocked ? getPhysicsAuthoringLockMessage(actionPhrase) : undefined,
  "aria-disabled": isBlocked || undefined,
});

export const usePhysicsAuthoringLockProps = (actionPhrase = "edit the scene") => {
  const isBlocked = usePhysicsAuthoringLock();
  return getPhysicsAuthoringLockProps(isBlocked, actionPhrase);
};

export const shouldBlockPhysicsMutation = (actionName: string): boolean => {
  if (!isPhysicsAuthoringLocked()) return false;
  logger.warn(
    readOnlyProjectMode
      ? `[PhysicsGuard] Blocked "${actionName}" in read-only project mode.`
      : `[PhysicsGuard] Blocked "${actionName}" while physics is running.`
  );
  showReadOnlyProjectBlockedToast();
  return true;
};

export const shouldBlockSceneAuthoringMutation = (actionName: string): boolean => {
  if (!isPhysicsAuthoringLocked()) return false;
  logger.warn(
    readOnlyProjectMode
      ? `[PhysicsGuard] Blocked scene edit "${actionName}" in read-only project mode.`
      : `[PhysicsGuard] Blocked scene edit "${actionName}" while physics is on.`
  );
  if (readOnlyProjectMode) {
    showReadOnlyProjectBlockedToast();
  } else {
    showSceneAuthoringBlockedToast();
  }
  return true;
};
